
// Task section 27: three DELIBERATELY separate validation layers, so a user
// can tell whether a problem is geometry, DANVA data, or XML/exchange - never
// merged into one opaque "invalid" flag.
import {findZeroLengthSegments, findSelfIntersections, findInvalidPolygons} from "../geometry/topologyQa.js";
import {getDanvaData} from "./DanvaDomainData.js";

// ── Level 1: PointView geometry ──────────────────────────────────────────
//
// Reuses the EXACT SAME existing checks the Topology-QA panel already runs
// project-wide (geometry/topologyQa.js) - never a second geometry-validity
// implementation. Applies them to a single feature (`[feature]`), since
// DANVA's per-object validation state (task section 28) needs a per-object
// answer, not a whole-project scan.
export function validateGeometry(feature){
	const issues = [];

	if(!feature.geometry || !feature.geometry.coordinates){
		issues.push({code: "missing-geometry", message: "Geometri mangler."});
		return {valid: false, issues};
	}

	for(const hit of findZeroLengthSegments([feature])){
		issues.push({code: "zero-length-segment", message: `Nul-længde segment ved vertex ${hit.vertexIndex}.`});
	}
	for(const hit of findSelfIntersections([feature])){
		issues.push({code: "self-intersection", message: "Utilsigtet selvskæring."});
	}
	for(const hit of findInvalidPolygons([feature])){
		issues.push({code: "invalid-polygon", message: "Ugyldigt polygon."});
	}

	return {valid: issues.length === 0, issues};
}

// ── Level 2: DANVA logical model ─────────────────────────────────────────
//
// Required attributes, valid codes, required relations, per the ACTIVE
// model's own object definition (via DanvaModelProvider - never a hardcoded
// per-type rule list, task section 10). Returns a clearly distinct
// "model-unavailable" result (not a silent pass, and not a fabricated
// failure) when no model is loaded - task section 53: this must stay honest
// about what it does and doesn't actually know yet.
export function validateDanvaObject(feature, modelProvider){
	const data = getDanvaData(feature);
	if(!data){
		return {applicable: false, valid: null, issues: [], reason: "not-a-danva-object"};
	}

	const def = modelProvider.getObjectDefinition(data.model, data.version, data.objectType);
	if(!def){
		return {
			applicable: true, valid: null, issues: [],
			reason: "model-unavailable",
			message: `Ingen modeldefinition fundet for ${data.model} ${data.version} / ${data.objectType} - kan ikke validere DANVA-data endnu.`,
		};
	}

	const issues = [];
	for(const attr of def.attributes){
		const value = data.attributes[attr.name];

		if(attr.required && (value === undefined || value === null || value === "")){
			issues.push({code: "required-attribute-missing", attribute: attr.name, message: `${attr.label} mangler.`});
			continue;
		}

		if(value !== undefined && value !== null && attr.dataType === "code" && attr.codeListId){
			const codeList = modelProvider.getCodeList(data.model, data.version, attr.codeListId);
			if(codeList && !codeList.some((c) => c.code === value)){
				issues.push({code: "invalid-code", attribute: attr.name, message: `${attr.label}: ugyldig kodeværdi "${value}".`});
			}
		}
	}

	for(const rel of def.relations){
		if(!rel.required) continue;
		const hasRelation = data.relations.some((r) => r.name === rel.name);
		if(!hasRelation) issues.push({code: "required-relation-missing", relation: rel.name, message: `Obligatorisk relation "${rel.name}" mangler.`});
	}

	return {applicable: true, valid: issues.length === 0, issues, reason: null};
}

// ── Level 3: Exchange / XSD ───────────────────────────────────────────────
//
// Adapter boundary (task section 32): "hvis browseren ikke realistisk kan
// udføre fuld XSD-validation korrekt, så design en klar adapter/service
// boundary fremfor en halv validator." No XSD library is vendored in this
// project and no backend/validation service exists (confirmed: this is a
// purely static, client-only build - see gulpfile.js/rollup.config.js and
// the absence of any server route anywhere in src/). Implementing a partial
// XSD validator here would be exactly the "halv validator" the task warns
// against, so this is an honest, clearly-typed "not available" result
// rather than a fake pass/fail - swapping in a real validator (a WASM
// libxml2 build, or a server-side validation endpoint) is the one thing
// that needs to change once one of those exists; every caller already
// handles `{available: false}` explicitly.
export function validateExchangeXml(){
	return {
		available: false,
		valid: null,
		issues: [],
		reason: "no-xsd-validator-configured",
		message: "Ingen XSD-validator er tilgængelig i dette miljø (intet XSD-bibliotek, ingen valideringsservice). Kræver enten et klient-bibliotek eller en server-side valideringstjeneste.",
	};
}
